// Runtime configuration resolved from the launch URL + build-time env. The
// launch token arrives as a query parameter from the operator's launch flow and
// is consumed immediately (never persisted). Generic URL/origin parsing is
// delegated to the shared @aerofruits/game-shell; this module only layers in the
// Dice-specific defaults (game id, fallback bet ladder).
import {
  resolveLaunchConfig,
  DEMO_BET_DENOMINATIONS,
  DEFAULT_BET_MINOR,
  type LaunchConfig,
} from '@aerofruits/game-shell';

export const GAME_ID = 'lightning-number-dice';

/** Injected at build time from VITE_GATEWAY_BASE_URL (see vite.config.ts). */
declare const __LIGHTNING_NUMBER_DICE_GATEWAY_DEFAULT__: string;

export interface RuntimeConfig extends LaunchConfig {
  /** Bet ladder shown in the UI (server remains authoritative). */
  betOptions: number[];
}

export { DEFAULT_BET_MINOR };

function envString(key: string, fallback: string): string {
  const v = (import.meta.env as Record<string, string | undefined>)[key];
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

export function resolveConfig(search: string = window.location.search): RuntimeConfig {
  const cfg = resolveLaunchConfig({
    search,
    // `gateway` selects the GAME-GATEWAY API base (NEVER the integration gateway).
    defaultGatewayBaseUrl: __LIGHTNING_NUMBER_DICE_GATEWAY_DEFAULT__,
    envParentOrigins: envString('VITE_ALLOWED_PARENT_ORIGINS', ''),
  });
  return { ...cfg, betOptions: [...DEMO_BET_DENOMINATIONS] };
}

/**
 * Builds an absolute, shareable replay URL for a persisted round. It points at
 * this app's standalone `replay.html` page and carries the public replay id
 * plus the (public, browser-facing) game-gateway base so the link works from a
 * fresh device with no active session. It contains NO launch/session token.
 */
export function buildReplayUrl(replayId: string, gatewayBaseUrl: string, base: string = window.location.href): string {
  const url = new URL('replay.html', base);
  url.search = '';
  url.searchParams.set('id', replayId);
  if (gatewayBaseUrl) url.searchParams.set('gateway', gatewayBaseUrl);
  return url.toString();
}

/** Resolves the standalone replay page's config: public id + gateway base + locale. */
export function resolveReplayConfig(search: string = window.location.search): {
  replayId: string;
  gatewayBaseUrl: string;
  locale?: string;
} {
  const params = new URLSearchParams(search);
  const gatewayBaseUrl = params.get('gateway') ?? __LIGHTNING_NUMBER_DICE_GATEWAY_DEFAULT__;
  const lang = (params.get('lang') ?? '').trim();
  return {
    replayId: (params.get('id') ?? '').trim(),
    gatewayBaseUrl,
    ...(lang ? { locale: lang } : {}),
  };
}
